import React, { useEffect, useRef, useState } from "react";
import {
  AskMeIcon,
  EmojiIcon,
  GalleryIcon,
  LinkIcon,
  MessageSendIcon,
} from "assets/icons";
import Button from "components/BaseWidgets/button";
import { suggestionMessages } from "../helper";
import { db } from "../../../../../firebase/firebaseChat";
import {
  doc,
  setDoc,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useSendMessage } from "hooks/api/useSendMessage";
import EmojiPicker from "emoji-picker-react";
import { useChatImage } from "hooks/api/useChat";
import { useQueryClient } from "@tanstack/react-query";

const MessageInput = ({
  messages,
  selectedChatId,
  currentUserId,
  onMessageSent,
  selectedThread,
  can_send_message,
  reqAccepted,
  chatDetail,
  userChats,
}) => {
  // console.log(chatDetail, "chatDetail");

  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const pickerRef = useRef(null);
  const buttonRef = useRef(null);
  const queryClient = useQueryClient();

  const [showPicker, setShowPicker] = useState(false);
  const handleEmojiClick = (emojiData) => {
    setMessage((prev) => prev + emojiData.emoji);
  };
  const selectedChat = userChats.find(
    (chat) => chat.id === selectedChatId || chat.chatId === selectedChatId
  );
  // console.log(currentUserId);
  // console.log(selectedChat);

  // Typing indication
  const typingTimeout = useRef(null);
  const updateTypingStatus = async (isTyping) => {
    if (!selectedChatId || !currentUserId) return;

    try {
      const chatRef = doc(db, "chats", selectedChat.id);

      // console.log(chatRef, "chatRef");

      await setDoc(
        chatRef,
        {
          typingStatus: {
            [currentUserId]: isTyping,
          },
        },
        { merge: true }
      );
    } catch (err) {
      console.error("typing status error", err);
    }
  };

  const chatIdRef = useRef();

  useEffect(() => {
    chatIdRef.current = selectedChatId;
  }, [selectedChatId]);
  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessage(value);

    updateTypingStatus(true);

    // clear previous timer
    if (typingTimeout.current) clearTimeout(typingTimeout.current);

    // typing ends after 1 second
    typingTimeout.current = setTimeout(() => {
      updateTypingStatus(false);
    }, 1000);
  };

  // ====================

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowPicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { mutateAsync: sendMessageApi } = useSendMessage(); // ✅ React Query mutation

  const { mutateAsync: sendChatImage } = useChatImage({
    onSuccess: (data) => console.log(data?.data?.image_url, "image_url"),
  });

  if (!selectedChatId) {
    return <div className="message-input mt-auto">No chat selected</div>;
  }
  const currentChat = Array.isArray(userChats)
    ? userChats.find((chat) => chat?.chatId === selectedChatId)
    : null;

  let receiverId = null;

  if (Array.isArray(currentChat?.users)) {
    receiverId = currentChat.users.find((id) => id !== currentUserId);
  } else if (
    currentChat?.participants &&
    typeof currentChat.participants === "object"
  ) {
    const ids = Object.keys(currentChat.participants);
    receiverId = ids.find((id) => id !== currentUserId);
  }

  // console.log(selectedThread, "receiverId");

  // 🟢 Send text message
  const handleSendMessage = async () => {
    if (!message.trim()) return;
    setSending(true);

    try {
      /** 1️⃣ SEND TO BACKEND */
      const apiResponse = await sendMessageApi({
        id: selectedThread?.aec_id, // receiver AEC ID
        message: message.trim(), // actual text
      });

      // console.log("API returned:", apiResponse);

      /** 2️⃣ WRITE TO FIRESTORE */
      const chatRef = doc(db, "chats", selectedChatId);

      await setDoc(
        chatRef,
        {
          lastMessage: message.trim(),
          lastMessageSender: currentUserId,
          lastMessageType: "text",
          lastMessageTime: serverTimestamp(),
        },
        { merge: true }
      );

      await addDoc(collection(chatRef, "messages"), {
        senderId: currentUserId,
        receiverId,
        messageId: Date.now().toString(),
        text: message.trim(),
        type: "text",
        isDeleted: false,
        mediaUrl: null,
        status: "unread",
        timestamp: serverTimestamp(),
      });

      await updateTypingStatus(false);
      setMessage("");
      onMessageSent?.();
    } catch (error) {
      console.error("❌ Error sending:", error);
    } finally {
      queryClient.invalidateQueries(["chat-threads"]);

      setSending(false);
    }
  };

  // 🟡 Send image message (via API → S3 → Firestore)
  // 🟡 Send image message (bypassing API → use dummy image URL)
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSending(true);
    try {
      const uploaded = await sendChatImage({
        id: selectedThread?.aec_id,
        message: "📷 Photo",
        file,
      });

      // console.log(uploaded, "uploaded");

      // 🧩 Step 1: Skip upload — use a test image URL
      const imageUrl =
        uploaded?.image_url ||
        uploaded?.data?.image_url ||
        uploaded?.url ||
        uploaded?.data?.url;

      const chatRef = doc(db, "chats", selectedChatId);

      // 🧩 Step 2: Update chat metadata
      await setDoc(
        chatRef,
        {
          lastMessage: "📷 Photo",
          lastMessageSender: currentUserId,
          lastMessageType: "image",
          lastMessageTime: serverTimestamp(),
        },
        { merge: true }
      );

      // 🧩 Step 3: Add message entry in Firestore
      await addDoc(collection(chatRef, "messages"), {
        senderId: currentUserId,
        receiverId,
        messageId: Date.now().toString(),
        text: null,
        type: "image",
        mediaUrl: imageUrl,
        isDeleted: false,
        status: "unread",
        timestamp: serverTimestamp(),
      });

      onMessageSent?.();
      // console.log("✅ Dummy image message sent:", imageUrl);
    } catch (error) {
      console.error("❌ Error sending dummy image:", error);
    } finally {
      setSending(false);
      e.target.value = "";
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSending(true);
    try {
      const uploaded = await sendChatImage({
        id: selectedThread?.aec_id,
        message: "📁 File",
        file,
      });

      // console.log(uploaded, "uploaded");

      // 🧩 Step 1: Skip upload — use a test image URL
      const imageUrl =
        uploaded?.image_url ||
        uploaded?.data?.image_url ||
        uploaded?.url ||
        uploaded?.data?.url;

      const chatRef = doc(db, "chats", selectedChatId);

      // 🧩 Step 2: Update chat metadata
      await setDoc(
        chatRef,
        {
          lastMessage: "📁 File",
          lastMessageSender: currentUserId,
          lastMessageType: "file",
          lastMessageTime: serverTimestamp(),
        },
        { merge: true }
      );

      // 🧩 Step 3: Add message entry in Firestore
      await addDoc(collection(chatRef, "messages"), {
        senderId: currentUserId,
        receiverId,
        messageId: Date.now().toString(),
        text: null,
        type: "file",
        mediaUrl: imageUrl,
        isDeleted: false,
        status: "unread",
        timestamp: serverTimestamp(),
      });

      onMessageSent?.();
      // console.log("✅ Dummy image message sent:", imageUrl);
    } catch (error) {
      console.error("❌ Error sending dummy image:", error);
    } finally {
      setSending(false);
      e.target.value = "";
    }
  };

  const handleSuggestionClick = async (text) => {
    setMessage(text);
  };

  const isRequestAccepted = !reqAccepted; // Placeholder: Replace with actual logic to check if request is accepted

  return (
    <>
      {/* Suggestion Buttons */}
      <div className="suggestion-section">
        <div className="scrollable-buttons">
          {suggestionMessages.map((text, index) => (
            <Button
              key={index}
              variant="tertiary"
              className="suggestion-btn"
              height={34}
              onClick={() => handleSuggestionClick(text)}
            >
              {text}
            </Button>
          ))}
          <AskMeIcon />
        </div>
      </div>

      {/* Message Input */}
      <div className="message-input">
        <div className="input-area">
          <div className="relative">
            <Button
              ref={buttonRef}
              size="none"
              className="emoji-btn"
              onClick={() => setShowPicker((prev) => !prev)}
            >
              <EmojiIcon />
            </Button>
            {showPicker && (
              <div ref={pickerRef} className="emoji-wrapper">
                <EmojiPicker onEmojiClick={handleEmojiClick} />
              </div>
            )}
          </div>
          <input
            onBlur={() => updateTypingStatus(false)}
            placeholder={
              isRequestAccepted
                ? "Write your message..."
                : "Request not accepted yet"
            }
            value={message}
            onChange={handleInputChange}
            onKeyDown={(e) =>
              isRequestAccepted && e.key === "Enter" && handleSendMessage()
            }
            disabled={sending || !isRequestAccepted}
          />

          <div className="icons">
            {/* <LinkIcon /> */}

            {/* Hidden file input for document/file upload */}
            <input
              type="file"
              accept=".pdf"
              id="file-upload"
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            <span
              onClick={() => document.getElementById("file-upload").click()}
              style={{ cursor: "pointer" }}
              title="Upload document"
            >
              <LinkIcon />
            </span>

            {/* Hidden file input for image upload */}
            <input
              type="file"
              accept="image/*"
              id="image-upload"
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
            <span
              onClick={() => document.getElementById("image-upload").click()}
              style={{ cursor: "pointer" }}
              title="Upload image"
            >
              <GalleryIcon />
            </span>

            <Button
              className="message-send"
              height="36px"
              icon={<MessageSendIcon />}
              onClick={handleSendMessage}
              disabled={sending || !can_send_message}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default MessageInput;
